import React, { useState, useEffect, useMemo } from "react";
import logo from "../assets/nbn logo.png";
import recurringdl from "../assets/recurringdl.png";
import arrowEmphasis from "../assets/arrow-for-emphasis.png";


import { useAppContext } from "../AppContext";
import FAQ from "./FAQ";
import { Select } from "antd";
import StripePayment from "./StripePayment";

const MONTH_NAMES = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Full state name -> 2-letter code for API lookup */
const STATE_NAME_TO_CODE = {
  Alabama: "AL", Alaska: "AK", Arizona: "AZ", Arkansas: "AR", California: "CA", Colorado: "CO",
  Connecticut: "CT", Delaware: "DE", "District of Columbia": "DC", Florida: "FL", Georgia: "GA",
  Hawaii: "HI", Idaho: "ID", Illinois: "IL", Indiana: "IN", Iowa: "IA", Kansas: "KS", Kentucky: "KY",
  Louisiana: "LA", Maine: "ME", Maryland: "MD", Massachusetts: "MA", Michigan: "MI", Minnesota: "MN",
  Mississippi: "MS", Missouri: "MO", Montana: "MT", Nebraska: "NE", Nevada: "NV", "New Hampshire": "NH",
  "New Jersey": "NJ", "New Mexico": "NM", "New York": "NY", "North Carolina": "NC", "North Dakota": "ND",
  Ohio: "OH", Oklahoma: "OK", Oregon: "OR", Pennsylvania: "PA", "Rhode Island": "RI", "South Carolina": "SC",
  "South Dakota": "SD", Tennessee: "TN", Texas: "TX", Utah: "UT", Vermont: "VT", Virginia: "VA",
  Washington: "WA", "West Virginia": "WV", Wisconsin: "WI", Wyoming: "WY", "Puerto Rico": "PR",
};

const LandingPage = () => {
  const { selectedState, setSelectedState, setSelectedPrice, setLeadsCount, setBuyNow, setZipcodes, loadingHeader, buyNow, billingFrequency, setBillingFrequency, stateAverages, setStateAverage } = useAppContext();

  const [monthlyStats, setMonthlyStats] = useState({ rows: [], from: null, to: null });
  const [monthlyStatsLoading, setMonthlyStatsLoading] = useState(true);
  const [monthlyStatsError, setMonthlyStatsError] = useState(null);

  const [byStateStats, setByStateStats] = useState({ currentMonth: null, rows: [] });
  const [byStateLoading, setByStateLoading] = useState(true);
  const [byStateError, setByStateError] = useState(null);

  const [estimatedLeadsAverage, setEstimatedLeadsAverage] = useState(null);
  const [estimatedLeadsLoading, setEstimatedLeadsLoading] = useState(false);
  const [estimatedLeadsError, setEstimatedLeadsError] = useState(null);

  // const apiBase = "https://app.commercialcleaningleadsforyou.com";
  const apiBase = "https://app.commercialcleaningleadsforyou.com";


  useEffect(() => {
    const fetchMonthlyStats = async () => {
      try {
        setMonthlyStatsLoading(true);
        setMonthlyStatsError(null);
        const res = await fetch(`${apiBase}/api/new-business-monthly`);
        if (!res.ok) throw new Error("Failed to fetch monthly stats");
        const data = await res.json();
        setMonthlyStats({ rows: data.rows || [], from: data.from, to: data.to });
      } catch (err) {
        setMonthlyStatsError(err.message);
        setMonthlyStats({ rows: [], from: null, to: null });
      } finally {
        setMonthlyStatsLoading(false);
      }
    };
    fetchMonthlyStats();
  }, [apiBase]);

  useEffect(() => {
    const fetchByStateStats = async () => {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1; // 1–12
      try {
        setByStateLoading(true);
        setByStateError(null);
        const res = await fetch(`${apiBase}/api/new-business-by-state`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ year, month }),
        });
        if (!res.ok) throw new Error("Failed to fetch by-state stats");
        const data = await res.json();
        setByStateStats({
          currentMonth: data.currentMonth ?? { year, month },
          rows: data.rows ?? [],
        });
      } catch (err) {
        setByStateError(err.message);
        setByStateStats({ currentMonth: null, rows: [] });
      } finally {
        setByStateLoading(false);
      }
    };
    fetchByStateStats();
  }, [apiBase]);

  useEffect(() => {
    if (!selectedState) {
      setEstimatedLeadsAverage(null);
      setEstimatedLeadsError(null);
      return;
    }
    const stateCode = STATE_NAME_TO_CODE[selectedState];
    if (!stateCode) return;
    const cached = stateAverages[stateCode];
    if (cached != null) {
      setEstimatedLeadsAverage(cached);
      setEstimatedLeadsError(null);
      setLeadsCount(cached);
      return;
    }
    let cancelled = false;
    setEstimatedLeadsLoading(true);
    setEstimatedLeadsError(null);
    fetch(`${apiBase}/api/new-business-state-average`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        state: stateCode,
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch average");
        return res.json();
      })
      .then((data) => {
        if (!cancelled && data.average_12_month != null) {
          setStateAverage(stateCode, data.average_12_month);
          setEstimatedLeadsAverage(data.average_12_month);
          setEstimatedLeadsError(null);
          setLeadsCount(data.average_12_month);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setEstimatedLeadsError(err.message);
          setEstimatedLeadsAverage(null);
        }
      })
      .finally(() => {
        if (!cancelled) setEstimatedLeadsLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedState, apiBase, setLeadsCount, stateAverages, setStateAverage]);

  // Pricing data from the image
  const pricingData = [
    // Left column
    { state: "Alabama", count: 400, cost: "$29/month" },
    { state: "Alaska", count: 8, cost: "$29/month" },
    { state: "Arizona", count: 268, cost: "$29/month" },
    { state: "Arkansas", count: 261, cost: "$29/month" },
    { state: "California", count: "", cost: "$99/month" },
    { state: "Colorado", count: 567, cost: "$79/month" },
    { state: "Connecticut", count: 335, cost: "$29/month" },
    { state: "Delaware", count: 134, cost: "$29/month" },
    { state: "Florida", count: 2855, cost: "$99/month" },
    { state: "Georgia", count: 1544, cost: "$99/month" },
    { state: "Hawaii", count: 99, cost: "$29/month" },
    { state: "Idaho", count: 81, cost: "$29/month" },
    { state: "Illinois", count: 1354, cost: "$99/month" },
    { state: "Indiana", count: 585, cost: "$79/month" },
    { state: "Iowa", count: 207, cost: "$29/month" },
    { state: "Kansas", count: 134, cost: "$29/month" },
    { state: "Kentucky", count: 321, cost: "$29/month" },
    { state: "Louisiana", count: 319, cost: "$29/month" },
    { state: "Maine", count: 775, cost: "$79/month" },
    { state: "Maryland", count: 626, cost: "$79/month" },
    { state: "Massachusetts", count: 14, cost: "$29/month" },
    { state: "Michigan", count: 1078, cost: "$99/month" },
    { state: "Minnesota", count: 472, cost: "$79/month" },
    { state: "Mississippi", count: 208, cost: "$29/month" },
    { state: "Missouri", count: 365, cost: "$29/month" },
    // Right column
    { state: "Montana", count: 83, cost: "$29/month" },
    { state: "Nebraska", count: 99, cost: "$29/month" },
    { state: "Nevada", count: 200, cost: "$29/month" },
    { state: "New Hampshire", count: 17, cost: "$29/month" },
    { state: "New Jersey", count: 1713, cost: "$99/month" },
    { state: "New Mexico", count: 200, cost: "$29/month" },
    { state: "New York", count: 1217, cost: "$99/month" },
    { state: "North Carolina", count: 795, cost: "$79/month" },
    { state: "North Dakota", count: 172, cost: "$29/month" },
    { state: "Ohio", count: 740, cost: "$79/month" },
    { state: "Oklahoma", count: 201, cost: "$29/month" },
    { state: "Oregon", count: 339, cost: "$29/month" },
    { state: "Pennsylvania", count: 1280, cost: "$99/month" },
    { state: "Rhode Island", count: 62, cost: "$29/month" },
    { state: "South Carolina", count: 640, cost: "$79/month" },
    { state: "South Dakota", count: 112, cost: "$29/month" },
    { state: "Tennessee", count: 839, cost: "$99/month" },
    { state: "Texas", count: 2175, cost: "$99/month" },
    { state: "Utah", count: 210, cost: "$79/month" },
    { state: "Vermont", count: 650, cost: "$79/month" },
    { state: "Virginia", count: 10, cost: "$29/month" },
    { state: "Washington", count: 709, cost: "$79/month" },
    { state: "West Virginia", count: 326, cost: "$29/month" },
    { state: "Wisconsin", count: 144, cost: "$29/month" },
    { state: "Wyoming", count: 74, cost: "$29/month" },
  ];

  const leftColumnData = pricingData.slice(0, 25);
  const rightColumnData = pricingData.slice(25);

  /** Month labels for by-state API: current, last, previous (e.g. Mar, Feb, Jan when current month is 3) */
  const byStateMonthLabels = useMemo(() => {
    const m = byStateStats.currentMonth?.month;
    if (m == null || m < 1 || m > 12) return null;
    return [
      MONTH_LABELS[(m - 3 + 12) % 12],
      MONTH_LABELS[(m - 2 + 12) % 12],
      MONTH_LABELS[m - 1]
      ,
    ];
  }, [byStateStats.currentMonth?.month]);

  /** Lookup by state code for by-state API rows */
  const byStateByCode = useMemo(() => {
    const map = {};
    (byStateStats.rows || []).forEach((r) => { map[r.state] = r; });
    return map;
  }, [byStateStats.rows]);

  /** Get count display for a state: use by-state API current_month when available, else static count */
  const getCountForState = (stateName) => {
    const code = STATE_NAME_TO_CODE[stateName];
    const row = code ? byStateByCode[code] : null;
    if (row != null) return row.current_month;
    const item = pricingData.find((s) => s.state === stateName);
    return item?.count;
  };

  /** Get three-month count display for table cell (e.g. "Mar: 97 | Feb: 370 | Jan: 259") */
  const getCountDisplayForState = (stateName) => {
    const code = STATE_NAME_TO_CODE[stateName];
    const row = code ? byStateByCode[code] : null;
    if (!row || !byStateMonthLabels) {
      const item = pricingData.find((s) => s.state === stateName);
      return formatCount(item?.count ?? "—");
    }
    const [cur, last, prev] = byStateMonthLabels;
    return `${formatCount(row.previous_last_month)} | ${formatCount(row.last_month)} | ${formatCount(row.current_month)}`;
  };

  const allStates = pricingData.map((item) => item.state);

  // Helper function to format numbers with commas
  const formatCount = (count) => {
    if (count === "" || count === null || count === undefined) {
      return "";
    }
    return typeof count === "number" ? count.toLocaleString() : count;
  };

  const monthLabel = (monthKey) =>
    typeof monthKey === "string" ? monthKey.charAt(0).toUpperCase() + monthKey.slice(1) : "";

  const handleStateSelect = (stateName) => {
    const s = pricingData.find(item => item.state === stateName);
    if (s) {
      setSelectedState(stateName);
      setZipcodes(stateName); // Using state name as "zipcode" for state-based orders

      // Parse cost like "$29/month" to 2900 (cents)
      const costInt = parseInt(s.cost.replace(/[^0-9]/g, "")) || 199;
      setSelectedPrice(costInt * 100);

      // Use by-state API current_month when available, else static count
      const count = getCountForState(stateName);
      setLeadsCount(typeof count === "number" ? count : 1000);
    }
  };

  const openCheckout = (stateName) => {
    handleStateSelect(stateName);
    setBuyNow("payment");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Section */}
      <div className="bg-white py-8 px-4 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start">
            {/* Left Content - Logo and Headline */}
            <div className="w-full lg:flex-initial lg:max-w-md lg:shrink-0">
              {/* Logo */}
              <div className="mb-6">
                <img src={logo} alt="NEW BUSINESS NOW" className="h-28 w-auto lg:h-auto lg:w-auto max-w-md" />
              </div>

              {/* Headline */}
              <div className="mt-8"></div>
              <p className="text-2xl lg:text-2xl font-semibold text-[#4E6978] mb-4 leading-tight">
                Get a List of<span className="text-[#F09900]"> Brand New Businesses <br /></span> In <span className="text-[#F09900]"> Your State in Seconds!</span>
              </p>

              {/* Sub-text */}
              <div className="mt-4">
                <p className="text-lg text-[#4E6978] font-semibold">
                  Weekly Updates Sent to You Automatically
                </p>
                <div className="mt-4">
                  <p className="text-lg text-[#4E6978] font-semibold">
                    All for a Small Monthly Fee - See Pricing Below.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Content - Lead Generation Form or Payment */}
            <div className={`w-full min-w-0 ${(buyNow === "payment" || buyNow === "download") ? "lg:flex-1 lg:min-w-[28rem]" : "lg:flex-initial lg:w-112 lg:ml-auto"}`}>
              {loadingHeader && (
                <div className="flex items-center justify-center min-h-[320px]">
                  <div className="w-10 h-10 border-4 border-gray-200 border-t-[#F09900] rounded-full animate-spin" />
                </div>
              )}
              {!loadingHeader && (buyNow === "payment" || buyNow === "download") && (
                <StripePayment />
              )}
              {!loadingHeader && buyNow !== "payment" && buyNow !== "download" && (
                <div className="border-2 border-[#4D4D4D] overflow-hidden rounded">
                  <div className="flex">
                    <div className="flex-1 bg-[#6B7B8A] text-white py-4 px-4 font-semibold text-base border-b-2 border-[#4D4D4D] text-center">
                      State
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-xl text-center font-bold text-[#4E6978] mb-4">
                      Get Your Leads Now
                    </p>

                    <div>
                      <p className="text-base text-[#4E6978] mb-3 font-normal text-center">
                        I would like to get a list leads every:
                      </p>
                      <div className="flex justify-center mb-4">
                        <span className="text-xl font-bold text-[#F09900]">Monthly</span>
                      </div>
                      <p className="text-base text-[#4E6978] mb-3 font-normal text-center">
                        for the state of:
                      </p>
                      <div className="mt-4 text-center">
                        <Select
                          showSearch
                          placeholder="Select a state"
                          className="w-[175px] h-10 custom-select"
                          value={selectedState || undefined}
                          onChange={handleStateSelect}
                          optionFilterProp="label"
                          filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                          options={pricingData.map(s => ({
                            label: s.state,
                            value: s.state
                          }))}
                        />

                        {selectedState && (
                          <div className="bg-[#F09900] border-2 border-[#4E6978] rounded-xl p-4 flex justify-between items-center mt-6 max-w-[400px] mx-auto">
                            <div className="flex flex-col text-left">
                              <span className="text-xs font-bold text-white uppercase tracking-wider">Estimated Businesses / Month</span>
                              <span className="text-xl font-black text-white text-center min-h-[28px] flex items-center justify-center">
                                {estimatedLeadsLoading ? (
                                  <span className="inline-block w-6 h-6 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                ) : (
                                  formatCount(estimatedLeadsAverage ?? getCountForState(selectedState)) || "Variable"
                                )}
                              </span>
                            </div>
                            <div className="flex flex-col ">
                              <span className="text-xs font-bold text-white uppercase tracking-wider">Monthly Cost</span>
                              <span className="text-xl font-black text-white text-center">
                                {pricingData.find(s => s.state === selectedState)?.cost.split('/')[0]}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* CTA Button */}
                    <div className="mt-4">
                      <button
                        disabled={!selectedState}
                        onClick={() => {
                          if (selectedState) setBuyNow('payment');
                        }}
                        className={`w-full border-2 border-[#4D6877] cursor-pointer font-bold py-2 px-5 rounded-md flex items-center justify-center gap-3 transition-colors mt-4 ${!selectedState ? 'bg-[#F09900] hover:bg-[#E08900] cursor-not-allowed opacity-50' : 'bg-[#F09900] hover:bg-[#E08900] text-white'}`}>
                        <img src={recurringdl} alt="Subscription icon" className="h-20 w-20 object-contain" />
                        <span className="text-lg font-bold text-white">Start Your {billingFrequency} Subscription</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white py-12 px-4 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <p className="text-xl lg:text-2xl font-bold text-[#F09900] mb-8 text-center">
            Never run out of new leads again with the Leads Now Brand New Business file.
          </p>

          <div className="space-y-6 my-8">
            <div className="flex items-center gap-4 py-4 bg-[#E5E5E5] border-2 border-[#F09900]">
              <img src={arrowEmphasis} alt="" className="w-10 h-10 object-contain flex-shrink-0" />
              <div>
                <p className="text-lg text-[#4E6978] flex-1 font-semibold">
                  Get brand new business phone contact information for newly registered and newly active businesses across the U.S. Updated weekly so you always targets fresh opportunities.
                </p>
                <p className="text-lg text-[#4E6978] flex-1 font-semibold">
                  Order by state, or combine multiple states in one file.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4 py-4 bg-[#E5E5E5] border-2 border-[#F09900]">
              <img src={arrowEmphasis} alt="" className="w-10 h-10 object-contain flex-shrink-0" />
              <p className="text-lg text-[#4E6978] flex-1 font-semibold">
                These aren't outdated lists — they are recently added business phone hookups identified within the last 7-30 days. The number of businesses listed depends on volume and market.
              </p>
            </div>

            <div className="flex items-center gap-4 py-4 bg-[#E5E5E5] border-2 border-[#F09900]">
              <img src={arrowEmphasis} alt="" className="w-10 h-10 object-contain flex-shrink-0" />
              <p className="text-lg text-[#4E6978] flex-1 font-semibold">
                Your first file is delivered instantly, then each week you'll get access to the newest leads in your area. The files are delivered in easy-to-use CSV format with business name, phone number, and address. Businesses so new, they only have a phone number!
              </p>
            </div>

            <div className="flex items-center gap-4 py-4 bg-[#E5E5E5] border-2 border-[#F09900]">
              <img src={arrowEmphasis} alt="" className="w-10 h-10 object-contain flex-shrink-0" />
              <p className="text-lg text-[#4E6978] flex-1 font-semibold">
                With our commercial cleaning-specific contact list, you're in control of your lead generation. Whether you're offering office cleaning, medical facility disinfecting, or industrial deep cleans, our data helps you connect with the right decision-makers faster.
              </p>
            </div>
          </div>

          <div className="text-center mb-8">
            <p className="text-2xl font-bold text-[#F09900] mb-6">
              Download your state today and start calling within the hour.
            </p>
            <div className="mt-8">
              <button className="bg-[#F09900] border-2 border-[#4D4D4D] hover:bg-[#E08900] text-white font-bold py-4 px-16 rounded-md text-xl transition-colors" style={{ color: 'white' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                START NOW
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Target Audience Section */}
      <div className="bg-[#E5E5E5] py-12 px-4 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <p className="text-xl lg:text-2xl font-bold text-[#F09900] mb-6 text-center underline">
            This list is perfect for
          </p>

       <div className="grid grid-cols-2 md:grid-cols-3 gap-4 my-6">
            <div className="bg-[#6B7B8A] hover:bg-[#5A6A79] text-white font-semibold py-5 px-6 rounded-md transition-colors text-center">
              Benefits Administration
            </div>
            <div className="bg-[#6B7B8A] hover:bg-[#5A6A79] text-white font-semibold py-5 px-6 rounded-md transition-colors text-center">
              Payroll/HR Services
            </div>
            <div className="bg-[#6B7B8A] hover:bg-[#5A6A79] text-white font-semibold py-5 px-6 rounded-md transition-colors text-center">
              Security Systems
            </div>
            <div className="bg-[#6B7B8A] hover:bg-[#5A6A79] text-white font-semibold py-5 px-6 rounded-md transition-colors text-center">
              Vending
            </div>
            <div className="bg-[#6B7B8A] hover:bg-[#5A6A79] text-white font-semibold py-5 px-6 rounded-md transition-colors text-center">
              Signage
            </div>
            <div className="bg-[#6B7B8A] hover:bg-[#5A6A79] text-white font-semibold py-5 px-6 rounded-md transition-colors text-center">
              Utilities
            </div>
          </div>


          <p className="text-[#4E6978] font-semibold text-center text-lg">
            And any other B2B service providers who need to reach new businesses quickly!.
          </p>
        </div>
      </div>

      {/* Get Started Section */}
       <div className="bg-[#4D4D4D] py-8 px-4 lg:px-8">
        <div className="mx-auto max-w-6xl text-center">
          <p className="text-3xl lg:text-4xl font-bold text-[#F09900] mb-4">
            Get Started:
          </p>
          <p className="text-xl text-white font-semibold mx-auto">
            Click on your state to start your weekly subscription and we'll do <br /> the rest!
          </p>
        </div>
      </div>

      {/* Pricing Table Section */}
      {/* <div className="bg-[#4D4D4D] py-12 px-4 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {countLoading && (
            <div className="flex justify-center py-8">
              <div className="w-10 h-10 border-4 border-gray-400 border-t-[#F09900] rounded-full animate-spin" />
            </div>
          )}
          {countError && (
            <p className="text-[#F09900] text-center py-4">Unable to load counts. Showing estimates.</p>
          )}
          {!countLoading && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-0 lg:gap-[120px]">
              <div>
                <div className="grid grid-cols-[auto_1fr_1fr_auto] gap-3 lg:gap-4 mb-2 items-baseline">
                  <div className="font-bold text-white text-lg">Cost:</div>
                  <div className="font-bold text-white text-lg">State:</div>
                  <div className="font-bold text-white text-lg min-w-[8rem]">
                    <span className="block mb-1">Monthly Counts</span>
                    <span className="text-white font-normal text-base">
                      {countData.monthly.length
                        ? countData.monthly.map((m, i) => (
                            <span key={`${m.year}-${m.month}`}>
                              {i > 0 && " | "}
                              {monthLabel(m.month)}
                            </span>
                          ))
                        : "—"}
                    </span>
                  </div>
                  <div className="font-bold text-white text-lg text-right">Cost:</div>
                </div>
                {countData.monthly.length > 0 && (
                  <div className="grid grid-cols-[auto_1fr_1fr_auto] gap-3 lg:gap-4 py-2 mb-2 border-b border-gray-500">
                    <div className="text-[#F09900] font-semibold">month</div>
                    <div className="text-white">Total</div>
                    <div className="flex justify-end gap-4 min-w-[8rem] text-white">
                      {countData.monthly.map((m) => (
                        <span key={`${m.year}-${m.month}`} className="tabular-nums text-right min-w-[2.5rem]">
                          {formatCount(m.count)}
                        </span>
                      ))}
                    </div>
                    <div className="text-[#F09900] text-right font-semibold tabular-nums">
                      {formatCount(countData.total_count)}
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  {leftColumnData.map((item, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-[auto_1fr_1fr_auto] gap-3 lg:gap-4 py-2 items-center"
                    >
                      <div className="text-[#F09900] font-semibold">month</div>
                      <div className="text-white">{item.state}</div>
                      <div className="flex justify-end gap-4 min-w-[8rem] text-white tabular-nums">
                        {countData.monthly.length
                          ? countData.monthly.map((_, i) => (
                              <span key={i} className="text-right min-w-[2.5rem]">
                                {formatCount(item.count)}
                              </span>
                            ))
                          : (
                            <span className="text-right">{formatCount(item.count)}</span>
                          )}
                      </div>
                      <div className="text-[#F09900] text-right font-semibold">
                        {item.cost}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="grid grid-cols-[auto_1fr_1fr_auto] gap-3 lg:gap-4 mb-2 items-baseline">
                  <div className="font-bold text-white text-lg">Cost:</div>
                  <div className="font-bold text-white text-lg">State:</div>
                  <div className="font-bold text-white text-lg min-w-[8rem]">
                    <span className="block mb-1">Monthly Counts</span>
                    <span className="text-white font-normal text-base">
                      {countData.monthly.length
                        ? countData.monthly.map((m, i) => (
                            <span key={`${m.year}-${m.month}`}>
                              {i > 0 && " | "}
                              {monthLabel(m.month)}
                            </span>
                          ))
                        : "—"}
                    </span>
                  </div>
                  <div className="font-bold text-white text-lg text-right">Cost:</div>
                </div>
                <div className="space-y-2">
                  {rightColumnData.map((item, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-[auto_1fr_1fr_auto] gap-3 lg:gap-4 py-2 items-center"
                    >
                      <div className="text-[#F09900] font-semibold">month</div>
                      <div className="text-white">{item.state}</div>
                      <div className="flex justify-end gap-4 min-w-[8rem] text-white tabular-nums">
                        {countData.monthly.length
                          ? countData.monthly.map((_, i) => (
                              <span key={i} className="text-right min-w-[2.5rem]">
                                {formatCount(item.count)}
                              </span>
                            ))
                          : (
                            <span className="text-right">{formatCount(item.count)}</span>
                          )}
                      </div>
                      <div className="text-[#F09900] text-right font-semibold">
                        {item.cost}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <div className="mt-5">
            <p className="text-white text-sm text-center">
              *Counts listed above are estimates based off a monthly average.
            </p>
          </div>
        </div>
      </div> */}

      <div className="bg-[#4D4D4D] py-12 px-4 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {byStateLoading && (
            <div className="flex justify-center py-4">
              <div className="w-10 h-10 border-4 border-gray-400 border-t-[#F09900] rounded-full animate-spin" />
            </div>
          )}
          {byStateError && (
            <p className="text-[#F09900] text-center py-2 text-sm">Unable to load live counts. Showing estimates.</p>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-[160px]">
            {/* Left Column */}
            <div>
              <div className="grid grid-cols-3 gap-4 mb-2 items-baseline">
                <div className="font-bold text-white text-lg">State:</div>
                <div className="font-bold text-white text-lg text-center">
                  <span className="block">Count*:</span>
                  {byStateMonthLabels && (
                    <span className="text-white font-normal text-sm block mt-0.5">
                      {byStateMonthLabels.join(" | ")}
                    </span>
                  )}
                </div>
                <div className="font-bold text-white text-lg text-right">Cost:</div>
              </div>
              <div className="space-y-2">
                {leftColumnData.map((item, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-3 gap-4 py-2 items-center"
                  >
                    <button
                      type="button"
                      onClick={() => openCheckout(item.state)}
                      className="text-white text-left hover:underline cursor-pointer transition-colors font-medium"
                      style={{color:'white'}}
                    >
                      {item.state}
                    </button>
                    <div className="text-white text-center text-sm">
                      {getCountDisplayForState(item.state)}
                    </div>
                    <button
                      type="button"
                      onClick={() => openCheckout(item.state)}
                      className="text-[#F09900] text-right font-semibold underline hover:underline cursor-pointer transition-colors text-right"
                      style={{color:'#F09900'}}
                    >
                      {item.cost}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div className="grid grid-cols-3 gap-4 mb-2 items-baseline">
                <div className="font-bold text-white text-lg">State:</div>
                <div className="font-bold text-white text-lg text-center">
                  <span className="block">Count*:</span>
                  {byStateMonthLabels && (
                    <span className="text-white font-normal text-sm block mt-0.5">
                      {byStateMonthLabels.join(" | ")}
                    </span>
                  )}
                </div>
                <div className="font-bold text-white text-lg text-right">Cost:</div>
              </div>
              <div className="space-y-2">
                {rightColumnData.map((item, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-3 gap-4 py-2 items-center"
                  >
                    <button
                      type="button"
                      onClick={() => openCheckout(item.state)}
                      className="text-white text-left hover:underline cursor-pointer transition-colors font-medium"
                      style={{color:'white'}}
                    >
                      {item.state}
                    </button>
                    <div className="text-white text-center text-sm">
                      {getCountDisplayForState(item.state)}
                    </div>
                    <button
                      type="button"
                      onClick={() => openCheckout(item.state)}
                      className="text-[#F09900] text-right font-semibold underline hover:underline cursor-pointer transition-colors text-right"
                      style={{color:'#F09900'}}
                    >
                      {item.cost}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
           <div className="mt-5">
          <p className="text-white text-sm text-center">
            *Counts listed above are estimates based off a monthly average.
          </p>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <FAQ />
    </div>
  );
};

export default LandingPage;

